import { chinaCalendarDay } from './china-holidays';
export type WorkdayPosition = { x: number; y: number };
export type WorkdaySettings = {
  version: 1;
  chinaHolidays: boolean;
  start: string;
  end: string;
  days: number[];
  enabled: boolean;
  collapsed: boolean;
  position: WorkdayPosition | null;
  lunchEnabled: boolean;
  lunchStart: string;
  lunchEnd: string;
  reminderEnabled: boolean;
  reminderMinutes: number;
};

export const DEFAULT_WORKDAY: WorkdaySettings = {
  version: 1,
  chinaHolidays: true,
  lunchEnabled: false,
  lunchStart: '12:00',
  lunchEnd: '13:00',
  reminderEnabled: false,
  reminderMinutes: 5,
  start: '09:00',
  end: '18:00',
  days: [1, 2, 3, 4, 5],
  enabled: true,
  collapsed: true,
  position: null,
};
export const WORKDAYS = [
  { value: 1, label: '一' },
  { value: 2, label: '二' },
  { value: 3, label: '三' },
  { value: 4, label: '四' },
  { value: 5, label: '五' },
  { value: 6, label: '六' },
  { value: 0, label: '日' },
];
export const validWorkTime = (value: unknown): value is string =>
  typeof value === 'string' && /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value);
export const workdayStorageKey = (userId?: string) =>
  'moyu:workday:v1:' + (userId ? 'account:' + encodeURIComponent(userId) : 'guest');
const clamp = (value: number) => Math.max(0, Math.min(1, value));

export function parseWorkdaySettings(raw: string | null): WorkdaySettings {
  try {
    const value = JSON.parse(raw || 'null');
    if (!value || value.version !== 1) return { ...DEFAULT_WORKDAY };
    const timesValid =
      validWorkTime(value.start) && validWorkTime(value.end) && value.start !== value.end;
    const days: number[] = Array.isArray(value.days)
      ? [
          ...new Set<number>(
            value.days.filter(
              (day: unknown) => Number.isInteger(day) && Number(day) >= 0 && Number(day) <= 6,
            ),
          ),
        ]
      : [];
    const point = value.position;
    return {
      version: 1,
      chinaHolidays: value.chinaHolidays !== false,
      lunchEnabled: value.lunchEnabled === true,
      lunchStart: validWorkTime(value.lunchStart) ? value.lunchStart : '12:00',
      lunchEnd: validWorkTime(value.lunchEnd) ? value.lunchEnd : '13:00',
      reminderEnabled: value.reminderEnabled === true,
      reminderMinutes: [5, 10, 15].includes(value.reminderMinutes) ? value.reminderMinutes : 5,
      start: timesValid ? value.start : DEFAULT_WORKDAY.start,
      end: timesValid ? value.end : DEFAULT_WORKDAY.end,
      days: days.length ? days : [...DEFAULT_WORKDAY.days],
      enabled: typeof value.enabled === 'boolean' ? value.enabled : true,
      collapsed: typeof value.collapsed === 'boolean' ? value.collapsed : true,
      position:
        point && Number.isFinite(point.x) && Number.isFinite(point.y)
          ? { x: clamp(point.x), y: clamp(point.y) }
          : null,
    };
  } catch {
    return { ...DEFAULT_WORKDAY };
  }
}

type Shift = { start: Date; end: Date };
export type WorkdayState = {
  phase: 'working' | 'before' | 'finished' | 'rest';
  remaining: number;
  progress: number;
  shift: Shift;
  lunch?: boolean;
  holiday?: string;
};

function atTime(day: Date, time: string, dayOffset = 0) {
  const [hours, minutes] = time.split(':').map(Number);
  // Calendar dates (not 24-hour offsets) keep local schedules correct across DST.
  return new Date(day.getFullYear(), day.getMonth(), day.getDate() + dayOffset, hours, minutes);
}

export function getWorkdayState(settings: WorkdaySettings, now: Date): WorkdayState {
  const current = now.getTime();
  const shifts: Shift[] = [];
  const calendar = settings.chinaHolidays ? chinaCalendarDay(now) : undefined;
  const isWorkingDay = (day: Date) => {
    const official = settings.chinaHolidays ? chinaCalendarDay(day) : undefined;
    if (official?.holiday) return false;
    if (official?.makeup) return true;
    return settings.days.includes(day.getDay());
  };
  for (let offset = -1; offset <= 370; offset++) {
    const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
    if (!isWorkingDay(day)) continue;
    shifts.push({
      start: atTime(day, settings.start),
      end: atTime(day, settings.end, settings.end < settings.start ? 1 : 0),
    });
  }
  const active = shifts.find((shift) => +shift.start <= current && current < +shift.end);
  if (active && !calendar?.holiday) {
    let lunchStart = atTime(active.start, settings.lunchStart);
    let lunchEnd = atTime(active.start, settings.lunchEnd);
    if (settings.end < settings.start && settings.lunchStart < settings.start) {
      lunchStart = atTime(active.start, settings.lunchStart, 1);
      lunchEnd = atTime(active.start, settings.lunchEnd, 1);
    }
    const hasLunch =
      settings.lunchEnabled &&
      +lunchStart >= +active.start &&
      +lunchEnd <= +active.end &&
      +lunchEnd > +lunchStart &&
      +lunchEnd - +lunchStart < +active.end - +active.start;
    const lunchMs = hasLunch ? +lunchEnd - +lunchStart : 0;
    const usedLunch = hasLunch ? Math.max(0, Math.min(current, +lunchEnd) - +lunchStart) : 0;
    return {
      phase: 'working',
      remaining: Math.max(0, Math.ceil((+active.end - current) / 1000)),
      progress: clamp(
        (current - +active.start - usedLunch) / (+active.end - +active.start - lunchMs),
      ),
      lunch: hasLunch && current >= +lunchStart && current < +lunchEnd,
      shift: active,
    };
  }
  const next = shifts.find((shift) => +shift.start > current)!;
  const finishedToday = shifts.some(
    (shift) => +shift.end <= current && shift.end.toDateString() === now.toDateString(),
  );
  return {
    phase: calendar?.holiday
      ? 'rest'
      : finishedToday
        ? 'finished'
        : isWorkingDay(now)
          ? 'before'
          : 'rest',
    holiday: calendar?.holiday,
    remaining: Math.max(0, Math.ceil((+next.start - current) / 1000)),
    progress: finishedToday ? 1 : 0,
    shift: next,
  };
}

export function formatWorkdayDuration(seconds: number) {
  const total = Math.max(0, Math.floor(seconds));
  return [Math.floor(total / 3600), Math.floor((total % 3600) / 60), total % 60]
    .map((part) => String(part).padStart(2, '0'))
    .join(':');
}

export function nextWorkdayLabel(start: Date, now: Date, time: string) {
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const day =
    start.toDateString() === now.toDateString()
      ? '今天'
      : start.toDateString() === tomorrow.toDateString()
        ? '明天'
        : '周' + ['日', '一', '二', '三', '四', '五', '六'][start.getDay()];
  return `${day} ${time}`;
}
